<template>
	<StandardModal
		open
		small
		:title="$t('onboarding_loginmethod_title')"
		:confirmText="$t(isLastStep ? 'common_button_done' : 'common_button_next')"
		:cancelText="$t('common_button_skip')"
		@confirm="onClickNext"
		@close="onClickClose"
		@cancel="onClickSkip"
		:confirmProps="{ loading: isLoading, disabled }"
		:cancelProps="{ link: true }"
		:hideCancel="currStep !== step.PROFILE_PIC || !disabled"
		ref="modal"
	>
		<div class="onboarding-wrapper">
			<ProgressBar v-if="hasMultipleSteps" :percent="progress" />
			<div
				v-if="currStep === step.PROFILE_PIC"
				class="onboarding-content-wrapper step-profile-pic"
			>
				<div class="title" v-text="$t('onboarding_orgwelcome_profile_title')" />
				<div
					class="description"
					v-text="$t('onboarding_orgwelcome_profile_description')"
				/>
				<ProfilePicker
					:user="user"
					:file="profileFile"
					size="100px"
					allowRemove
					@success="onUploadSuccess"
					@remove="onRemoveProfile"
				/>
			</div>
			<div
				v-if="currStep === step.PRONOUNS"
				class="onboarding-content-wrapper step-pronouns"
			>
				<div
					class="title"
					v-text="$t('onboarding_orgwelcome_pronouns_title')"
				/>
				<div
					class="description"
					v-text="$t('onboarding_orgwelcome_profile_visibility')"
				/>
				<div class="pronouns-wrapper">
					<Input
						:placeholder="$t('settings_profile_pronouns_placeholder')"
						v-model="pronouns"
						autoFocus
						maxlength="40"
					/>
					<div class="pronoun-examples">
						<div v-text="$t('onboarding_orgwelcome_pronouns_example_label')" />
						<div
							class="pronoun-example"
							v-text="$t('onboarding_orgwelcome_pronouns_example_he')"
							@click="
								onClickExample($t('onboarding_orgwelcome_pronouns_example_he'))
							"
						/>
						<div
							class="pronoun-example"
							v-text="$t('onboarding_orgwelcome_pronouns_example_she')"
							@click="
								onClickExample($t('onboarding_orgwelcome_pronouns_example_she'))
							"
						/>
						<div
							class="pronoun-example"
							v-text="$t('onboarding_orgwelcome_pronouns_example_they')"
							@click="
								onClickExample(
									$t('onboarding_orgwelcome_pronouns_example_they')
								)
							"
						/>
					</div>
				</div>
			</div>
			<div
				v-if="currStep === step.TITLE"
				class="onboarding-content-wrapper step-title"
			>
				<div class="title" v-text="$t('onboarding_orgwelcome_title_title')" />
				<div
					class="description"
					v-text="$t('onboarding_orgwelcome_profile_visibility')"
				/>
				<div class="title-wrapper">
					<Input
						:placeholder="$t('settings_profile_title_placeholder')"
						v-model="title"
						autoFocus
						maxlength="40"
					/>
				</div>
			</div>
		</div>

		<template #header>
			<!-- Hide modal header to provide extra space -->
			<div />
		</template>

		<template #footer-left v-if="hasMultipleSteps && !isFirstStep">
			<Button
				link
				@click="onClickBack"
				:icon="ChevronIcon"
				:iconProps="{ left: true, type: 'regular' }"
			>
				{{ $t('common_button_back') }}
			</Button>
		</template>
	</StandardModal>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import {
	StandardModal,
	Input,
	ProgressBar,
	ProfilePicker,
	Button,
} from '@/components/common'
import { getRequiredOnboardingSteps } from '@/helpers/startup'
import { onboardingSteps as step } from '@/helpers/constants'
import { ChevronIcon } from '@/icons'
import { getMyUser } from '@/store'
import { updateUser, updateClientMeta } from '@/api/user'
import { createLogger } from '@/helpers/log'

const log = createLogger('OnboardingModal', true)
const emit = defineEmits(['close'])

const user = getMyUser()
const currIndex = ref(0)
const isLoading = ref(false)
const profilePic = ref(null)
const pronouns = ref(null)
const title = ref(null)
const modal = ref(null)
const steps = ref(getRequiredOnboardingSteps())

const currStep = computed(() => steps.value[currIndex.value])
const hasMultipleSteps = computed(() => steps.value.length > 1)
const isFirstStep = computed(() => currIndex.value === 0)
const isLastStep = computed(() => currIndex.value === steps.value.length - 1)
const progress = computed(() =>
	Math.round(((currIndex.value + 1) / steps.value.length) * 100)
)
const disabled = computed(() => {
	if (currStep.value === step.PROFILE_PIC) return !profilePic.value
	return false
})
const profileFile = computed(() => profilePic.value?.file || null)

onMounted(() => {
	log(`modal mounted with ${steps.value.length} steps`, steps.value)
	trackPresented()
})

async function onClickNext() {
	await saveStep()
	if (currIndex.value < steps.value.length - 1) {
		currIndex.value++
		log('proceeded to next step', currIndex.value)
		await nextTick()
		trackPresented()
	} else {
		// we've reached the end so we can just close
		log('proceeded to end so closing')
		emit('close')
	}
}

async function onClickSkip() {
	log('clicked skip')
	if (currIndex.value < steps.value.length - 1) {
		currIndex.value++
		await nextTick()
		await trackPresented()
	} else {
		// we've reached the end so we can just close
		log('skipped to end so closing')
		emit('close')
	}
}

function onClickClose() {
	/* do nothing */
}

function onClickBack() {
	log('clicked back')
	if (currIndex.value > 0) {
		currIndex.value--
	}
}

function onClickExample(text) {
	log('clicked example', text)
	pronouns.value = text
}

function onUploadSuccess(upload) {
	profilePic.value = upload || null
}

function onRemoveProfile() {
	profilePic.value = null
}

async function saveStep() {
	if (currStep.value === step.PROFILE_PIC && profilePic.value?.key) {
		await saveUserData({ profilepic: profilePic.value.key })
	} else if (currStep.value === step.PRONOUNS && pronouns.value) {
		await saveUserData({ pronouns: pronouns.value })
	} else if (currStep.value === step.TITLE && title.value) {
		await saveUserData({ title: title.value })
	}
}

async function trackPresented() {
	if (currStep.value === step.PROFILE_PIC) {
		await saveClientMeta({
			onboarding_profilepic_last_presented_date: new Date(),
		})
	} else if (currStep.value === step.PRONOUNS) {
		await saveClientMeta({
			onboarding_pronouns_last_presented_date: new Date(),
		})
	} else if (currStep.value === step.TITLE) {
		await saveClientMeta({
			onboarding_title_last_presented_date: new Date(),
		})
	}
}

async function saveUserData(data) {
	try {
		log('saving user data', data)
		isLoading.value = true
		await updateUser({ ...data, user_id: user.id })
	} catch (e) {
		console.error(e)
		modal.value.$alert.error(e)
	}
	isLoading.value = false
}

async function saveClientMeta(client_meta) {
	try {
		log('saving client_meta', client_meta)
		await updateClientMeta(client_meta)
	} catch (e) {
		// a failed dismissal write means the popup may show again next login —
		// better than rejecting out of onClickSkip/onClickNext and wedging the modal
		console.error(e)
	}
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';
.onboarding-wrapper {
	padding: 16px;
	.onboarding-content-wrapper {
		@include flex-column-center(4px);
		padding: 32px 16px 0 16px;

		.title {
			@include fontFamily(800);
			font-size: 24px;
			text-align: center;
		}
		.description {
			color: var(--neutral-600);
			text-align: center;
		}

		&.step-profile-pic {
			.pronto-profile-picker {
				margin-top: 24px;
			}
			.description {
				margin-bottom: 16px;
				@include fontFamily(400);
			}
		}

		&.step-pronouns,
		&.step-title {
			.description {
				margin-bottom: 16px;
			}
			.pronouns-wrapper,
			.title-wrapper {
				width: 380px;
				margin-top: 24px;
			}
		}

		&.step-pronouns {
			.pronouns-wrapper {
				.pronoun-examples {
					@include flex(8px);
					margin-top: 8px;
					.pronoun-example {
						cursor: pointer;
						@include fontFamily(900);
						@include radius;
						font-size: 12px;
						padding: 8px;
						color: var(--blue-400);
						background-color: var(--blue-100);
						&:hover {
							background-color: var(--blue-200);
						}
					}
				}
			}
		}
	}
}
.pronto-progress {
	background-color: var(--neutral-100);
}
</style>
